import { View, Text, StyleSheet, Pressable, ScrollView, useWindowDimensions, Animated, Platform } from 'react-native';
import { useEffect, useRef } from 'react';
import { theme } from '@/constants/theme';
import { useAccessibility } from '@/contexts/AccessibilityContext';

interface QuickRepliesProps {
  options: string[];
  onSelect: (option: string) => void;
}

const VIOLET        = '#7C3AED';
const CHIP_BG       = 'rgba(124, 58, 237, 0.14)';
const CHIP_BORDER   = 'rgba(124, 58, 237, 0.50)';
const CHIP_TEXT     = '#C4B5FD';
const CHIP_SHADOW   = 'rgba(124, 58, 237, 0.22)';

export function QuickReplies({ options, onSelect }: QuickRepliesProps) {
  const { settings }   = useAccessibility();
  const { width }      = useWindowDimensions();
  const isMobile       = width < 768;
  const animatedValues = useRef<Animated.Value[]>([]);

  useEffect(() => {
    animatedValues.current = options.map(() => new Animated.Value(0));
    Animated.stagger(70, animatedValues.current.map(anim =>
      Animated.spring(anim, { toValue: 1, tension: 100, friction: 11, useNativeDriver: true })
    )).start();
  }, [options]);

  if (options.length === 0) return null;

  return (
    <View style={styles.wrapper} accessible accessibilityRole="menu" accessibilityLabel="תשובות מהירות">
      <View style={styles.topLine} />
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.container}>
        {options.map((option, index) => {
          const animValue = animatedValues.current[index] ?? new Animated.Value(1);
          return (
            <Animated.View key={index} style={{
              opacity:   animValue,
              transform: [
                { translateY: animValue.interpolate({ inputRange: [0,1], outputRange: [18,0] }) },
                { scale:      animValue.interpolate({ inputRange: [0,1], outputRange: [0.88,1] }) },
              ],
            }}>
              <Pressable
                style={({ pressed }) => [styles.chip, isMobile && styles.chipMobile, pressed && styles.chipPressed]}
                onPress={() => onSelect(option)}
                accessible accessibilityRole="menuitem"
                accessibilityLabel={`תשובה מהירה: ${option}`}
              >
                <View style={styles.dot} />
                <Text style={[styles.chipText, isMobile && styles.chipTextMobile]} numberOfLines={2} ellipsizeMode="tail">
                  {option}
                </Text>
              </Pressable>
            </Animated.View>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: '#14132A',
    width:           '100%',
    borderTopWidth:  1,
    borderTopColor:  '#2D2B50',
  },
  topLine: { height: 2, backgroundColor: VIOLET, opacity: 0.35 },
  container: {
    paddingHorizontal: theme.spacing.md,
    paddingTop:        10,
    paddingBottom:     12,
    gap:               8,
    flexDirection:     'row',
    flexWrap:          'wrap',
    justifyContent:    'center',
    alignItems:        'center',
  },
  chip: {
    flexDirection:     'row-reverse',
    alignItems:        'center',
    gap:               7,
    backgroundColor:   CHIP_BG,
    paddingHorizontal: 16,
    paddingVertical:   11,
    borderRadius:      14,
    borderWidth:       1.5,
    borderColor:       CHIP_BORDER,
    width:             190,
    minHeight:         50,
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 2px 10px ${CHIP_SHADOW}` } as any
      : { shadowColor: VIOLET, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.20, shadowRadius: 6, elevation: 3 }),
  },
  chipMobile:  { paddingHorizontal: 13, paddingVertical: 9, width: 162, minHeight: 46, borderRadius: 12 },
  chipPressed: { backgroundColor: 'rgba(124,58,237,0.28)', transform: [{ scale: 0.96 }], borderColor: VIOLET },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: VIOLET, opacity: 0.80, flexShrink: 0 },
  chipText:       { color: CHIP_TEXT, fontFamily: theme.fonts.bold, fontSize: 13, textAlign: 'right', lineHeight: 18, flex: 1, writingDirection: 'rtl' },
  chipTextMobile: { fontSize: 12, lineHeight: 17 },
});